import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { STORAGE_KEYS, USER_ROLES } from '../config';
import SettingsMenu from './SettingsMenu';
import { useI18n } from '../i18n';
import './Navbar.css';
import './Home.css';

const Home = () => {
  const username = localStorage.getItem(STORAGE_KEYS.username) || 'guest';
  const role = localStorage.getItem(STORAGE_KEYS.role) || USER_ROLES.user;
  const isLoggedIn = localStorage.getItem(STORAGE_KEYS.isLoggedIn) === 'true';
  const registryUrl = localStorage.getItem(STORAGE_KEYS.registryUrl) || '—';
  const navigate = useNavigate();
  const { t, basePath } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const roleLabel = role === USER_ROLES.siteAdmin
    ? '系统管理员'
    : role === USER_ROLES.competitionAdmin
      ? '赛事管理员'
      : '普通用户';

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.isLoggedIn);
    localStorage.removeItem(STORAGE_KEYS.username);
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.registryUrl);
    localStorage.removeItem(STORAGE_KEYS.role);
    navigate(`${basePath}/leaderboard`);
  };

  const closeMenu = () => setIsMenuOpen(false);

  const handleToggleMenu = (e) => {
    e.stopPropagation();
    setIsMenuOpen(prev => !prev);
  };

  // 点击页面其他区域时关闭菜单
  React.useEffect(() => {
    if (!isMenuOpen) return;
    
    const handleClickOutside = (e) => {
      // 如果点击的是菜单内部，不关闭
      const navLinks = document.querySelector('.nav-links');
      const menuToggle = document.querySelector('.menu-toggle');
      if (navLinks?.contains(e.target) || menuToggle?.contains(e.target)) {
        return;
      }
      setIsMenuOpen(false);
    };
    
    // 延迟添加监听器，避免与当前点击事件冲突
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="home-page">
      <nav className="navbar">
        <div className="nav-brand">
          <img className="nav-logo" src="/asynscale_ai_logo.png" alt="AsynScale AI Logo" />
          <span className="nav-title">Bench2Drive Leaderboard</span>
        </div>
        
        <button className="menu-toggle" onClick={handleToggleMenu} aria-label="Toggle menu">
          {isMenuOpen ? '✕' : '☰'}
        </button>

        <div className={`nav-links ${isMenuOpen ? 'open' : ''}`}>
          <Link to={`${basePath}/leaderboard`} className="nav-link active" onClick={closeMenu}>{t('nav.home')}</Link>
          <Link to={`${basePath}/leaderboard/competitions`} className="nav-link" onClick={closeMenu}>{t('nav.competitions')}</Link>
          <SettingsMenu />
          {!isLoggedIn ? (
            <Link to={`${basePath}/leaderboard/login`} className="nav-link nav-login" onClick={closeMenu}>{t('nav.login')}</Link>
          ) : (
            <div className="user-menu">
              <div className="user-menu-trigger">👤 {username}</div>
              <div className="user-menu-panel">
                <div className="user-menu-row">
                  <span className="user-menu-label">用户名</span>
                  <span>{username}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">角色</span>
                  <span>{roleLabel}</span>
                </div>
                <div className="user-menu-row">
                  <span className="user-menu-label">Registry</span>
                  <span>{registryUrl}</span>
                </div>
                <button className="user-menu-logout" onClick={() => { handleLogout(); closeMenu(); }}>退出登录</button>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="home-hero-block">
        <div className="home-hero">
          <div className="hero-content">
            <h1>{t('home.title')}</h1>
            <p className="hero-subtitle">{t('home.subtitle')}</p>
            <div className="hero-buttons">
              <Link to={`${basePath}/leaderboard/competitions`} className="primary-btn">{t('home.cta.comp')}</Link>
              <Link to={`${basePath}/leaderboard/get-started`} className="secondary-btn">{t('home.cta.getStarted')}</Link>
              <a
                href="https://thinklab-sjtu.github.io/Bench2Drive/"
                target="_blank"
                rel="noopener noreferrer"
                className="ghost-btn"
              >
                {t('home.cta.official')}
              </a>
            </div>
          </div>
          <div className="hero-image" aria-hidden="true">
            <img className="theme-dark-img" src={`${process.env.PUBLIC_URL}/pictures/autodrive_home.png`} alt="" />
            <img className="theme-light-img" src={`${process.env.PUBLIC_URL}/pictures/autodrive_integration_white.png`} alt="" />
          </div>
        </div>
      </div>

      <div className="section-block">
        <section className="home-section features-section">
          <h2>{t('home.features')}</h2>
          <div className="features-grid">
            <div className="feature-card">
              <img src="/pictures/multi_ability_benchmark.jpg" alt={t('home.feature.multi.title')} />
              <div className="feature-overlay">
                <h3>{t('home.feature.multi.title')}</h3>
                <p>{t('home.feature.multi.desc')}</p>
              </div>
            </div>
            <div className="feature-card">
              <img src="/pictures/think2drive_integration.jpg" alt={t('home.feature.think.title')} />
              <div className="feature-overlay">
                <h3>{t('home.feature.think.title')}</h3>
                <p>{t('home.feature.think.desc')}</p>
              </div>
            </div>
            <div className="feature-card">
              <img src="/pictures/advanced_metrics.jpg" alt={t('home.feature.metrics.title')} />
              <div className="feature-overlay">
                <h3>{t('home.feature.metrics.title')}</h3>
                <p>{t('home.feature.metrics.desc')}</p>
              </div>
            </div>
            <div className="feature-card">
              <img src="/pictures/220_challenge_routes.jpg" alt={t('home.feature.routes.title')} />
              <div className="feature-overlay">
                <h3>{t('home.feature.routes.title')}</h3>
                <p>{t('home.feature.routes.desc')}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="section-block">
        <section className="home-section about-section">
          <h2>{t('home.about.title')}</h2>
          <p>{t('home.about.p1')}</p>
          <p>{t('home.about.p2')}</p>
        </section>
      </div>

      <div className="section-block">
        <section className="home-section overview-section">
          <h2>{t('home.overview')}</h2>
          <div className="overview-container">
            <img src="/overview.png" alt="Bench2Drive Project Overview" />
          </div>
        </section>
      </div>

      <div className="section-block">
        <section className="home-section media-section">
          <h2>{t('home.video')}</h2>
          <div className="video-wrapper">
            <iframe
              title="Bench2Drive Introduction Video"
              src="https://www.youtube.com/embed/-osdzJJs2g0"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </section>
      </div>

      <div className="section-block">
        <section className="home-section cta-section">
          <h2>{t('home.ready')}</h2>
          <p>{t('home.ready.desc')}</p>
          <Link to={`${basePath}/leaderboard/competitions`} className="primary-btn">{t('home.ready.cta')}</Link>
        </section>
      </div>
    </div>
  );
};

export default Home;
